import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { type BadgeTone, StatusBadge } from "components/ui/StatusBadge";
import type { MatchingResultItem } from "hooks/use-free-matching-api";
import type { FC } from "react";
import { useState } from "react";
import { formatToManYen } from "utils/text-utils";

type ScoreBarProps = {
  label: string;
  score: number | null;
};

const ScoreBar: FC<ScoreBarProps> = ({ label, score }) => {
  if (score === null) return null;
  const pct = Math.round(score * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-16 text-gray-500">{label}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-500 h-2 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-8 text-right text-gray-600">{pct}%</span>
    </div>
  );
};

/**
 * @param {number} score - スコア値
 * @return {BadgeTone} tone
 */
function getScoreTone(score: number): BadgeTone {
  if (score >= 0.8) return "soft-success";
  if (score >= 0.6) return "soft-warning";
  return "soft-error";
}

type StarRatingProps = {
  rating: number;
  onRate: (rating: number) => void;
  disabled?: boolean;
};

const StarRating: FC<StarRatingProps> = ({ rating, onRate, disabled }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          className={`text-lg leading-none transition-colors ${
            star <= (hover || rating) ? "text-yellow-400" : "text-gray-300"
          } ${disabled ? "cursor-not-allowed" : "cursor-pointer hover:scale-110"}`}
          onMouseEnter={() => !disabled && setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onRate(star)}
        >
          ★
        </button>
      ))}
    </div>
  );
};

/**
 * 案件 depth のラベルを取得
 * @param {number} depth - 案件 depth 値
 * @return {string} ラベル文字列
 */
function getMatterDepthLabel(depth: number): string {
  return `${depth}次請け`;
}

/**
 * 人材 depth のラベルを取得（OpenSearch 保存値スケール）
 * @param {number} depth - 人材 depth 値（0=送信元BP直属, 1=1社先, N=N社先）
 * @return {string} ラベル文字列
 */
function getHrDepthLabel(depth: number): string {
  if (depth === 0) return "直所属";
  return `${depth}社先`;
}

/**
 * total depth に基づく tone を取得
 * @param {number} total - total depth
 * @return {BadgeTone} tone
 */
function getDepthTone(total: number): BadgeTone {
  if (total <= 2) return "soft-success";
  if (total === 3) return "soft-info";
  if (total === 4) return "soft-warning";
  return "soft-error";
}

export type MatchingResultCardProps = {
  result: MatchingResultItem;
  onPropose: (result: MatchingResultItem) => void;
  onCompare: (result: MatchingResultItem) => void;
  onFeedback?: (
    resultId: string,
    rating: number,
    comment?: string | null,
  ) => Promise<void>;
  onAddToPipeline?: (result: MatchingResultItem) => void;
  isAddingToPipeline?: boolean;
};

/**
 * マッチング結果カードの件名リンク遷移先メールIDを決定する。
 * - preset トリガーは起点メール (sourceEmailId) へ遷移
 * - auto/manual/freetext は候補メール (matchedEmailId) へ遷移
 * @param {MatchingResultItem} result マッチング結果
 * @return {string | null} 遷移先メールID（なければ null）
 */
function getLinkEmailId(result: MatchingResultItem): string | null {
  if (result.triggerType === "preset") {
    return result.sourceEmailId || null;
  }
  return result.matchedEmailId || null;
}

/**
 * マッチング結果カードの件名に表示する文字列を決定する。
 * preset 時は matched_subject がプリセット名になるため、
 * 代わりに起点メールの subject を表示する。
 * @param {MatchingResultItem} result マッチング結果
 * @return {string} 表示する件名
 */
function getDisplaySubject(result: MatchingResultItem): string {
  if (result.triggerType === "preset") {
    const snapshotSubject = (
      result.sourceSnapshot as { subject?: string } | null
    )?.subject;
    return snapshotSubject || result.sourceSubject || result.matchedSubject;
  }
  return result.matchedSubject;
}

/**
 * マッチング結果カード
 * @param {MatchingResultCardProps} props コンポーネントプロパティ
 * @return {JSX.Element} マッチング結果カード
 */
export const MatchingResultCard: FC<MatchingResultCardProps> = ({
  result,
  onPropose,
  onCompare,
  onFeedback,
  onAddToPipeline,
  isAddingToPipeline,
}) => {
  const totalPct = Math.round(result.totalScore * 100);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState(result.feedback?.comment ?? "");
  const [saving, setSaving] = useState(false);
  const linkEmailId = getLinkEmailId(result);
  const displaySubject = getDisplaySubject(result);

  const handleRate = async (rating: number) => {
    if (!onFeedback || saving) return;
    setSaving(true);
    try {
      await onFeedback(result.id, rating, result.feedback?.comment ?? null);
    } finally {
      setSaving(false);
    }
  };

  const handleCommentSave = async () => {
    if (!onFeedback || !result.feedback || saving) return;
    setSaving(true);
    try {
      await onFeedback(result.id, result.feedback.rating, comment || null);
      setShowComment(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 border border-gray-100">
      {/* ヘッダー: 件名 + スコア */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0 mr-3">
          <div className="text-sm truncate">
            {linkEmailId ? (
              <Link to={`/emails/search/${linkEmailId}`}>{displaySubject}</Link>
            ) : (
              <span className="text-gray-700">{displaySubject}</span>
            )}
          </div>
        </div>
        <StatusBadge tone={getScoreTone(result.totalScore)}>
          {totalPct}%
        </StatusBadge>
      </div>

      {/* 個別スコア */}
      <div className="space-y-1 mb-3">
        <ScoreBar label="スキル" score={result.skillScore} />
        <ScoreBar label="単価" score={result.rewardScore} />
        <ScoreBar label="勤務地" score={result.locationScore} />
        <ScoreBar label="稼働" score={result.availabilityScore} />
        <ScoreBar label="商流" score={result.depthScore} />
      </div>

      {/* 単価内訳 */}
      {(() => {
        const sourceSnap = result.sourceSnapshot as Record<
          string,
          unknown
        > | null;
        const matchedSnap = result.matchedSnapshot as Record<
          string,
          unknown
        > | null;
        const isMatterSource = result.sourceType === "matter";
        const matterSnap = isMatterSource ? sourceSnap : matchedSnap;
        const hrSnap = isMatterSource ? matchedSnap : sourceSnap;
        const matterReward = (matterSnap?.reward as number | null) ?? null;
        const hrReward = (hrSnap?.reward as number | null) ?? null;

        if (matterReward == null && hrReward == null) return null;

        const isOverBudget =
          matterReward != null && hrReward != null && hrReward > matterReward;

        return (
          <div className="mb-3 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-500">単価:</span>
              {matterReward != null && (
                <span className="badge badge-xs badge-outline">
                  案件 {formatToManYen(matterReward)}
                </span>
              )}
              {hrReward != null && (
                <span className="badge badge-xs badge-outline">
                  人材 {formatToManYen(hrReward)}
                </span>
              )}
            </div>
            {isOverBudget && (
              <div className="mt-1 text-warning flex items-center gap-1">
                <span>⚠️</span>
                <span>人材の希望単価が案件予算を超過しています</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* 商流の深さ内訳 */}
      {(() => {
        const sourceSnap = result.sourceSnapshot as Record<
          string,
          unknown
        > | null;
        const matchedSnap = result.matchedSnapshot as Record<
          string,
          unknown
        > | null;
        const isMatterSource = result.sourceType === "matter";
        const matterSnap = isMatterSource ? sourceSnap : matchedSnap;
        const hrSnap = isMatterSource ? matchedSnap : sourceSnap;
        const matterDepth = (matterSnap?.depth as number | null) ?? null;
        const hrDepth = (hrSnap?.depth as number | null) ?? null;
        const maxHrDepth = (matterSnap?.max_hr_depth as number | null) ?? null;
        const totalDepth =
          matterDepth != null && hrDepth != null ? matterDepth + hrDepth : null;
        const isOverLimit =
          maxHrDepth != null && hrDepth != null && hrDepth > maxHrDepth;

        if (matterDepth == null && hrDepth == null) return null;

        return (
          <div className="mb-3 text-xs">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-gray-500">商流:</span>
              {matterDepth != null && (
                <span className="badge badge-xs badge-outline">
                  案件 {getMatterDepthLabel(matterDepth)}
                </span>
              )}
              {hrDepth != null && (
                <span className="badge badge-xs badge-outline">
                  人材 {getHrDepthLabel(hrDepth)}
                </span>
              )}
              {totalDepth != null && (
                <StatusBadge tone={getDepthTone(totalDepth)}>
                  = 商流 {totalDepth}
                </StatusBadge>
              )}
            </div>
            {isOverLimit && (
              <div className="mt-1 text-warning flex items-center gap-1">
                <span>⚠️</span>
                <span>
                  商流制限超過（上限 {getHrDepthLabel(maxHrDepth!)} に対し{" "}
                  {getHrDepthLabel(hrDepth!)}）
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {/* AI 分析 */}
      {result.aiReasoning && (
        <div className="mb-3">
          <p className="text-xs text-gray-600">{result.aiReasoning}</p>
        </div>
      )}

      {/* ハイライト & リスク */}
      {(result.aiHighlights?.length || result.aiRisks?.length) && (
        <div className="flex flex-wrap gap-1 mb-3">
          {result.aiHighlights?.map(h => (
            <StatusBadge key={h} tone="soft-success">
              {h}
            </StatusBadge>
          ))}
          {result.aiRisks?.map(r => (
            <StatusBadge key={r} tone="soft-warning">
              {r}
            </StatusBadge>
          ))}
        </div>
      )}

      {/* フィードバック */}
      {onFeedback && (
        <div className="mb-3 border-t border-gray-100 pt-2">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">評価:</span>
            <StarRating
              rating={result.feedback?.rating ?? 0}
              onRate={handleRate}
              disabled={saving}
            />
            {result.feedback && (
              <button
                type="button"
                className="text-xs text-gray-400 hover:text-gray-600 ml-auto"
                onClick={() => setShowComment(!showComment)}
              >
                {showComment ? "閉じる" : "コメント"}
              </button>
            )}
          </div>
          {showComment && result.feedback && (
            <div className="mt-2 flex gap-2">
              <textarea
                className="textarea textarea-bordered textarea-xs flex-1"
                rows={2}
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="コメントを入力..."
                maxLength={2000}
              />
              <div className="self-end">
                <Button
                  type="button"
                  size="xs"
                  onClick={handleCommentSave}
                  disabled={saving}
                >
                  保存
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* アクションボタン */}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          size="xs"
          onClick={() => onCompare(result)}
          disabled={!linkEmailId}
          type="button"
        >
          詳細比較
        </Button>
        {onAddToPipeline && (
          <Button
            variant="success"
            isOutline
            size="xs"
            onClick={() => onAddToPipeline(result)}
            disabled={isAddingToPipeline}
            type="button"
          >
            {isAddingToPipeline ? <Loading size="xs" /> : "営業進捗に追加"}
          </Button>
        )}
        {result.status === "proposed" ? (
          <StatusBadge tone="tag-neutral">提案済み</StatusBadge>
        ) : (
          <Button
            variant="secondary"
            size="xs"
            onClick={() => onPropose(result)}
            type="button"
          >
            提案する
          </Button>
        )}
      </div>
    </div>
  );
};
